import { BridgeComponent } from "@hotwired/hotwire-native-bridge"
import { canvasResize, PHOTO_THUMBNAIL } from "../lib/image_resize"
import { csrfToken, postJson, post } from "../lib/http"
import { uploadThumbnailsInChunks } from "../lib/thumbnail_backfill"

export default class extends BridgeComponent {
  static component = "photo-library"

  static targets = [
    "scanButton", "selectButton", "progress", "progressBar", "progressText",
    "stats", "totalCount", "gpsCount", "waitingSection", "zeroGpsError", "gpsUploadedCount"
  ]

  static values = {
    uploadUrl: String
  }

  _photoAccountID() {
    const value = document.querySelector('meta[name="photo-updates-account"]')?.content
    return value ? Number(value) : null
  }

  connect() {
    super.connect()
    // this.bridge is only set when the native bridge is available.
    // On web, super.connect() returns early but JS continues here,
    // so guard explicitly before touching the DOM.
    if (!this.bridge) return

    window.photoFileMap = window.photoFileMap || {}
    this.totalGpsUploaded = 0

    // Reveal the native upload section and hide the web upload form
    this.element.classList.remove("hidden")
    const webUpload = document.querySelector('[data-controller="photo-upload"]')
    if (webUpload) webUpload.classList.add("hidden")
  }

  scan() {
    if (!this.hasScanButtonTarget) return

    this.scanButtonTarget.disabled = true
    this.scanButtonTarget.textContent = "Scanning library..."

    this.send("scan", {}, (response) => {
      // Find the photos array (may be at different nesting levels)
      const photos = response?.data?.photos || response?.photos
      const error = response?.error || response?.data?.error

      if (error) {
        this.handleScanError(error)
      } else if (Array.isArray(response)) {
        this.processScanResults(response)
      } else if (photos) {
        this.processScanResults(photos)
      } else {
        this.handleNoPhotosFound()
      }
    })
  }

  selectPhotos() {
    if (!this.hasSelectButtonTarget) return

    this.selectButtonTarget.disabled = true

    this.send("selectPhotos", { accountID: this._photoAccountID() }, (response) => {
      const photos = response?.data?.photos || response?.photos
      const error = response?.error || response?.data?.error

      if (error === "choose_photos") {
        // User chose "Choose Photos" from action sheet — open picker via separate bridge event
        this.pickPhotos()
      } else if (error) {
        this.handleSelectError(error)
      } else if (Array.isArray(response) && response.length > 0) {
        this.processScanResults(response)
      } else if (photos && photos.length > 0) {
        this.processScanResults(photos)
      } else {
        // Picker was cancelled or no photos selected — just reset buttons
        this.resetButtons()
      }
    })
  }

  pickPhotos() {
    if (!this.hasSelectButtonTarget) return

    this.selectButtonTarget.disabled = true

    this.send("pickPhotos", { accountID: this._photoAccountID() }, (response) => {
      const photos = response?.data?.photos || response?.photos
      const error = response?.error || response?.data?.error

      if (error) {
        this.handleSelectError(error)
      } else if (Array.isArray(response) && response.length > 0) {
        this.processScanResults(response)
      } else if (photos && photos.length > 0) {
        this.processScanResults(photos)
      } else {
        this.resetButtons()
      }
    })
  }

  async processScanResults(photos) {
    // Handle empty results
    if (!photos || photos.length === 0) {
      this.handleNoPhotosFound()
      return
    }

    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove("hidden")
    }
    if (this.hasStatsTarget) {
      this.statsTarget.classList.remove("hidden")
    }

    // Update total count
    if (this.hasTotalCountTarget) {
      this.totalCountTarget.textContent = photos.length
    }
    if (this.hasGpsCountTarget) {
      this.gpsCountTarget.textContent = photos.length
    }

    // Hide any previous zero-GPS error
    if (this.hasZeroGpsErrorTarget) this.zeroGpsErrorTarget.classList.add("hidden")

    // Store localIdentifiers for later photo fetching
    // We mark them as "pending" so room_controller knows to fetch via bridge
    for (const photo of photos) {
      window.photoFileMap[photo.client_ref] = {
        localIdentifier: photo.client_ref,
        pending: true
      }
    }

    // Home page flow: create room first, then upload metadata and navigate
    if (!this.uploadUrlValue) {
      await this.createRoomAndUpload(photos)
      return
    }

    // Room page flow: upload metadata directly
    await this.uploadPhotoBatches(photos)

    // Call finalize to mark participant as ready
    // Matching runs synchronously (perform_now in dev) during finalize, so by the
    // time finalize returns the matching_complete broadcast has already fired.
    // The backfill is handled by room_controller's onMatchingComplete handler,
    // which is the only reliable point where matching is guaranteed done.
    const roomCodeMatch = this.uploadUrlValue.match(/\/rooms\/([A-Za-z0-9]{6})\//)
    if (roomCodeMatch) {
      await post(`/rooms/${roomCodeMatch[1]}/photo_metadata/finalize`)
    }

    this.showWaitingSection(photos.length)
  }

  setStatus(text) {
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = text
    }
  }

  async createRoomAndUpload(photos) {
    try {
      this.setStatus("Creating room...")

      // Create the room
      const response = await fetch("/rooms", {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken(),
          "Accept": "text/html"
        },
        redirect: "follow"
      })

      if (!response.ok) {
        this.setStatus("Room creation failed: " + response.status)
        this.resetButtons()
        return
      }

      // Extract room code from the redirect URL (e.g. /rooms/A3BF2K)
      const match = response.url.match(/\/rooms\/([A-Za-z0-9]{6})/)
      if (!match) {
        this.setStatus("No room code in URL: " + response.url)
        this.resetButtons()
        return
      }

      const roomCode = match[1]
      const uploadUrl = `/rooms/${roomCode}/photo_metadata/batch_create`

      // Upload metadata batches
      this.setStatus("Uploading photos...")
      this.uploadUrlValue = uploadUrl
      await this.uploadPhotoBatches(photos)

      // Finalize upload (marks participant as ready)
      this.setStatus("Finalizing...")
      await post(`/rooms/${roomCode}/photo_metadata/finalize`)

      // Navigate to the room page (Turbo.visit preserves window.photoFileMap)
      // Backfill is handled by room_controller's onMatchingComplete once on room page.
      this.setStatus("Opening room " + roomCode + "...")
      window.Turbo.visit(`/rooms/${roomCode}`)
    } catch (error) {
      this.setStatus("Error: " + error.message)
      this.resetButtons()
    }
  }

  async uploadPhotoBatches(photos) {
    // Metadata here carries no thumbnails (those backfill separately), so large
    // batches are cheap — the server bulk-upserts each one. Was 20, far too small.
    const BATCH_SIZE = 250
    const total = photos.length

    for (let i = 0; i < photos.length; i += BATCH_SIZE) {
      const batch = photos.slice(i, i + BATCH_SIZE)
      await this.sendBatch(batch)

      const processed = Math.min(i + BATCH_SIZE, total)
      this.updateProgress(processed, total)
    }
  }

  resetButtons() {
    if (this.hasScanButtonTarget) {
      this.scanButtonTarget.disabled = false
      this.scanButtonTarget.textContent = "Scan Entire Library"
      this.scanButtonTarget.classList.remove("hidden")
    }
    if (this.hasSelectButtonTarget) {
      this.selectButtonTarget.disabled = false
      this.selectButtonTarget.classList.remove("hidden")
    }
  }

  handleNoPhotosFound() {
    // Hide progress indicators
    if (this.hasProgressTarget) this.progressTarget.classList.add("hidden")
    if (this.hasStatsTarget) this.statsTarget.classList.add("hidden")

    // Re-enable buttons
    if (this.hasScanButtonTarget) {
      this.scanButtonTarget.disabled = false
      this.scanButtonTarget.textContent = "Scan Entire Library"
      this.scanButtonTarget.classList.remove("hidden")
    }
    if (this.hasSelectButtonTarget) {
      this.selectButtonTarget.disabled = false
      this.selectButtonTarget.classList.remove("hidden")
    }

    // Show inline error instead of alert
    if (this.hasZeroGpsErrorTarget) {
      this.zeroGpsErrorTarget.classList.remove("hidden")
    }
  }

  async sendBatch(batch) {
    await postJson(this.uploadUrlValue, { metadata: batch })
  }

  updateProgress(current, total) {
    const pct = Math.round((current / total) * 100)

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${pct}%`
    }
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${current}/${total}`
    }
  }

  showWaitingSection(newCount = 0) {
    if (this.hasScanButtonTarget) {
      this.scanButtonTarget.classList.add("hidden")
    }
    if (this.hasSelectButtonTarget) {
      this.selectButtonTarget.classList.add("hidden")
    }
    // Accumulate GPS count across multiple selections
    this.totalGpsUploaded += newCount
    if (this.hasGpsUploadedCountTarget) {
      this.gpsUploadedCountTarget.textContent = this.totalGpsUploaded
    }
    if (this.hasWaitingSectionTarget) {
      this.waitingSectionTarget.classList.remove("hidden")
    }
  }

  addMorePhotos() {
    if (this.hasWaitingSectionTarget) this.waitingSectionTarget.classList.add("hidden")
    if (this.hasZeroGpsErrorTarget) this.zeroGpsErrorTarget.classList.add("hidden")
    if (this.hasProgressTarget) this.progressTarget.classList.add("hidden")
    if (this.hasStatsTarget) this.statsTarget.classList.add("hidden")
    // Go straight to picker — no need to show the select button first
    this.selectPhotos()
  }

  // Called by room_controller's onMatchingComplete: after matching runs, fetch and
  // store thumbnails for matched photos that have none (typically iCloud-only
  // photos that couldn't be thumbnailed during the metadata scan).
  async _backfillMatchThumbnails() {
    try {
      // Room-scoped URL when we have a room code, so the server only returns
      // client_refs for photos matched in this specific room.
      const roomCodeMatch = this.uploadUrlValue.match(/\/rooms\/([A-Za-z0-9]{6})\//)
      const missingUrl = roomCodeMatch
        ? `/rooms/${roomCodeMatch[1]}/match_missing_thumbnails`
        : "/library_photos/match_missing_thumbnails"

      const res = await fetch(missingUrl, { headers: { "X-CSRF-Token": csrfToken() } })
      const { client_refs } = await res.json()
      await uploadThumbnailsInChunks(client_refs, (ref) => this._fetchThumbnailData(ref))
    } catch (e) {
      console.warn("[photo-library] backfill thumbnails failed:", e)
    }
  }

  _fetchThumbnailData(localIdentifier) {
    return new Promise((resolve) => {
      this.send("fetchPhoto", { localIdentifier, accountID: this._photoAccountID() }, (response) => {
        const b64  = response?.base64 || response?.data?.base64
        const mime = response?.mimeType || response?.data?.mimeType || "image/jpeg"
        if (!b64) return resolve(null)
        resolve(canvasResize(`data:${mime};base64,${b64}`, PHOTO_THUMBNAIL))
      })
    })
  }

  // Called by room_controller when a match is found and we need the actual photo
  async fetchPhotoForUpload(clientRef) {
    return new Promise((resolve, reject) => {
      this.send("fetchPhoto", { localIdentifier: clientRef }, (response) => {
        const error = response?.error || response?.data?.error
        if (error) {
          reject(new Error(error))
          return
        }

        // Find the base64 data (may be at different nesting levels)
        const base64Data = response?.base64 || response?.data?.base64
        const mimeType = response?.mimeType || response?.data?.mimeType || "image/jpeg"

        if (!base64Data) {
          reject(new Error("No base64 data in response"))
          return
        }

        try {
          // Clean the base64 string and convert to Blob for upload
          const cleanBase64 = base64Data.replace(/\s/g, '')
          const byteCharacters = atob(cleanBase64)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          resolve(new Blob([byteArray], { type: mimeType }))
        } catch (e) {
          reject(e)
        }
      })
    })
  }

  handleScanError(error) {
    if (error === "permission_denied") {
      alert("Photo library access denied. Please enable it in Settings.")
    } else {
      alert("Failed to scan photo library: " + error)
    }

    if (this.hasScanButtonTarget) {
      this.scanButtonTarget.disabled = false
      this.scanButtonTarget.textContent = "Scan Entire Library"
    }
  }

  handleSelectError(error) {
    if (error === "permission_denied") {
      alert("Photo library access denied. Please enable it in Settings.")
    } else {
      alert("Failed to select photos: " + error)
    }

    if (this.hasSelectButtonTarget) {
      this.selectButtonTarget.disabled = false
    }
  }
}
